import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import HeaderLanding from '../components/partials/HeaderLanding';
import SmartsuppChat from '../components/external/SmartsuppChat';
import { useNavigate } from 'react-router-dom';

const TermsOfService = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openDropdowns, setOpenDropdowns] = useState({});
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const sectionRefs = useRef({});
  const sidebarRef = useRef(null);
  const navigate = useNavigate();

  // Section configuration with hierarchy
  const sections = [
    { id: 'introduction', title: 'Introduction', level: 1 },
    { id: 'about-us', title: 'About Us', level: 1 },
    { id: 'user-registration', title: 'User Registration', level: 1 },
    { id: 'use-of-software', title: 'Use of Software', level: 1 },
    { 
      id: 'escrow-services', 
      title: 'Escrow Services & Transaction Terms', 
      level: 1,
      subsections: [
        { id: 'escrow-process', title: 'Escrow Process' },
        { id: 'dispute-timeline', title: 'Dispute Timeline' }
      ]
    },
    { 
      id: 'dispute-resolution', 
      title: 'Dispute Resolution', 
      level: 1,
      subsections: [
        { id: 'initial-resolution', title: 'Initial Resolution' },
        { id: 'formal-dispute', title: 'Formal Dispute Filing' },
        { id: 'ai-mediation', title: 'AI-Assisted Mediation' },
        { id: 'human-review', title: 'Human Review & Decision' }
      ]
    },
    { 
      id: 'data-protection', 
      title: 'Data Protection & Rights', 
      level: 1,
      subsections: [
        { id: 'information-collection', title: 'Information Collection' },
        { id: 'user-rights', title: 'Your Rights' }
      ]
    },
    { id: 'business-verification', title: 'Business Verification', level: 1 },
    { id: 'prohibited-use', title: 'Prohibited Use', level: 1 },
    { id: 'subscription-fees', title: 'Subscription Fees & Charges', level: 1 },
    { 
      id: 'refund-policy', 
      title: 'Refund Policy', 
      level: 1,
      subsections: [
        { id: 'for-buyers', title: 'For Buyers' },
        { id: 'for-vendors', title: 'For Vendors' },
        { id: 'complaint-and-refund', title: "Complaints and Refund Process" }
      ]
    },
    { id: 'online-payments', title: 'Online Payments', level: 1 },
    { id: 'api-access', title: 'API Access', level: 1 },
    { id: 'cancellation-accounts', title: 'Cancellation Of Accounts', level: 1 },
    { id: 'use-cookies', title: 'Use of Cookies', level: 1 },
    { id: 'disclaimer-liability', title: 'Disclaimer & Limitation of Liability', level: 1 },
    { id: 'copyright', title: 'Copyright', level: 1 },
    { id: 'applicable-law', title: 'Applicable Law', level: 1 },
    { id: 'electronic-signature', title: 'Electronic Signature', level: 1 },
    { id: 'updates-modifications', title: 'Updates and Modifications', level: 1 },
    { id: 'feedback', title: 'Feedback', level: 1 }
  ];

  const toggleDropdown = (sectionId) => {
    setOpenDropdowns(prev => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const scrollToSection = (sectionId) => {
    const element = sectionRefs.current[sectionId];
    if (element) {
      const offset = 80;
      const elementPosition = element.offsetTop - offset;
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
      setIsSidebarOpen(false); // Close sidebar on mobile after clicking
    }
  };

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };

  // Close sidebar when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (sidebarRef.current && !sidebarRef.current.contains(event.target)) {
        setIsSidebarOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    const observerOptions = {
      root: null,
      rootMargin: '-20% 0px -70% 0px',
      threshold: 0
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, observerOptions);

    sections.forEach(section => {
      const element = sectionRefs.current[section.id];
      if (element) {
        observer.observe(element);
      }
    });

    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <HeaderLanding />

      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-20 left-4 z-40">
        <button
          onClick={toggleSidebar}
          className="bg-blue-600 text-white p-3 rounded-lg shadow-lg hover:bg-blue-700 transition-colors"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>
      </div>

      {/* Overlay */}
      {isSidebarOpen && (
        <div className="lg:hidden fixed inset-0 bg-black bg-opacity-50 z-30" onClick={() => setIsSidebarOpen(false)} />
      )}

      <div className="container mx-auto px-4 py-8 pt-20 lg:pt-8">
        <div className="flex flex-col lg:flex-row gap-8 max-w-7xl mx-auto">
          {/* Sidebar Navigation */}
          <div 
            ref={sidebarRef}
            className={`lg:w-80 flex-shrink-0 transform transition-transform duration-300 ease-in-out ${
              isSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'
            } fixed lg:static top-0 left-0 h-full lg:h-auto z-40 w-80 lg:w-auto bg-white lg:bg-transparent`}
          >
            <div className="sticky top-32 bg-white rounded-xl shadow-sm border border-gray-200 p-6 h-[calc(100vh-8rem)] lg:h-auto overflow-y-auto">
              {/* Mobile Close Button */}
              <div className="flex items-center justify-between mb-4 lg:hidden">
                <h3 className="text-lg font-semibold text-gray-900 flex items-center">
                  <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                  Table of Contents
                </h3>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 text-gray-500 hover:text-gray-700"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Desktop Title */}
              <h3 className="text-lg font-semibold text-gray-900 mb-4 lg:flex items-center hidden">
                <svg className="w-5 h-5 mr-2 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
                Table of Contents
              </h3>

              <nav className="space-y-2">
                {sections.map((section) => (
                  <div key={section.id} className="space-y-1">
                    <button
                      onClick={() => {
                        scrollToSection(section.id);
                        if (section.subsections) {
                          toggleDropdown(section.id);
                        }
                      }}
                      className={`w-full text-left px-3 py-3 lg:py-2 rounded-lg transition-all duration-200 flex items-center justify-between group ${
                        activeSection === section.id
                          ? 'bg-blue-50 text-blue-700 border-l-4 border-blue-600'
                          : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                      }`}
                    >
                      <span className="font-medium text-sm lg:text-xs xl:text-sm">{section.title}</span>
                      {section.subsections && (
                        <svg
                          className={`w-4 h-4 transition-transform duration-200 ${
                            openDropdowns[section.id] ? 'rotate-180' : ''
                          }`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                        </svg>
                      )}
                    </button>
                    
                    {section.subsections && openDropdowns[section.id] && (
                      <div className="ml-4 space-y-1 border-l-2 border-gray-200 pl-2">
                        {section.subsections.map((subsection) => (
                          <button
                            key={subsection.id}
                            onClick={() => scrollToSection(subsection.id)}
                            className={`w-full text-left px-3 py-2 lg:py-1.5 rounded-lg transition-all duration-200 text-sm ${
                              activeSection === subsection.id
                                ? 'text-blue-600 bg-blue-50 font-medium'
                                : 'text-gray-600 hover:text-gray-900 hover:bg-white'
                            }`}
                          >
                            {subsection.title}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 lg:ml-0">
            <div className="max-w-4xl mx-auto">
              {/* Download PDF Button */}
              <div className="text-center mb-8">
                <a
                  href="/assets/Terms of Service _ Paymeo.pdf"
                  className="inline-flex items-center px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-medium rounded-lg shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:from-blue-700 hover:to-blue-800 text-sm lg:text-base"
                  download
                >
                  <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Download as PDF
                </a>
              </div>
              
              {/* Terms Container */}
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                {/* Header */}
                <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 lg:p-8 text-center">
                  <img 
                    src="https://cdn.paymeo.co/uploads/brand/1788303507596_8dfca1b253b2139805.png" 
                    alt="Paymeo Logo" 
                    className="mx-auto w-12 h-12 lg:w-16 lg:h-16 bg-white rounded-2xl p-2 shadow-lg" 
                  />
                  <h1 className="text-2xl lg:text-4xl font-bold mt-4 lg:mt-6 mb-2">Terms of Service & Privacy Policy</h1>
                  <p className="text-blue-100 text-sm lg:text-lg">Last Updated: November 2025</p>
                </div>

                <div className="p-4 lg:p-8">
                  {/* Introduction */}
                  <section 
                    id="introduction"
                    ref={el => sectionRefs.current.introduction = el}
                    className="mb-8 lg:mb-12 scroll-mt-20 lg:scroll-mt-24"
                  >
                    <p className="text-base lg:text-lg text-gray-700 mb-4 lg:mb-6 leading-relaxed">
                      Please read carefully. This document explains our obligations as a service provider and yours as a customer.
                    </p>
                    <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                      By using or accessing any element of the website (paymeo.co) or Paymeo mobile application, you indicate that you have read, understood, and agree to be bound by these Terms of Service ("Terms"). These Terms govern your use of the Product and form a legal contract between Meo Technologies Limited ("Paymeo", "we" or "us") and you ("Customer" or "you"). If you are an individual accepting these Terms on behalf of a company or entity, then you are binding the company or entity to these Terms and represent and warrant that you have full power and authority to do so.
                    </p>
                  </section>

                  {/* About Us */}
                  <section 
                    id="about-us"
                    ref={el => sectionRefs.current['about-us'] = el}
                    className="mb-8 lg:mb-12 scroll-mt-20 lg:scroll-mt-24"
                  >
                    <h2 className="text-xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 pb-2 border-b-2 border-blue-100">About Us</h2>
                    <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                      Paymeo is an intent-driven social commerce platform that connects buyers and sellers through AI-powered request routing, secured escrow payments, and social commerce tools. Our platform enables businesses and individuals to discover, negotiate, and transact with trust and efficiency.
                    </p>
                  </section>

                  {/* User Registration */}
                  <section 
                    id="user-registration"
                    ref={el => sectionRefs.current['user-registration'] = el}
                    className="mb-8 lg:mb-12 scroll-mt-20 lg:scroll-mt-24"
                  >
                    <h2 className="text-xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 pb-2 border-b-2 border-blue-100">User Registration</h2>
                    <p className="text-gray-600 leading-relaxed text-sm lg:text-base">
                      To use Paymeo, you have to create an account by registering. To register, you will need to provide certain information such as your email, first name, last name, business name and phone number and we may seek to verify your information (by ourselves or through third parties). You give us permission to do all these. Our website and services are not directed to children under 18. We do not knowingly transact or provide any services to children under 18.
                    </p>
                  </section>

                  {/* Use of Software */}
                  <section 
                    id="use-of-software"
                    ref={el => sectionRefs.current['use-of-software'] = el}
                    className="mb-8 lg:mb-12 scroll-mt-20 lg:scroll-mt-24"
                  >
                    <h2 className="text-xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 pb-2 border-b-2 border-blue-100">Use of Software</h2>
                    <p className="text-gray-600 leading-relaxed text-sm lg:text-base mb-4 lg:mb-6">
                      Paymeo grants you the right to access and use the Service via the Website and Mobile Application with the particular user roles available to you. Your rights to use the Product are non-exclusive, non-transferable and non-sublicenseable. You will not (or permit anyone else to):
                    </p>
                    <ul className="grid gap-2 lg:gap-3">
                      {[
                        "rent, lease, reproduce, modify, create derivative works of, distribute or transfer the Product;",
                        "use the Product for the benefit of any third party or incorporate the Product into any other product or service;",
                        "circumvent mechanisms in the Product intended to limit your use;",
                        "reverse engineer, disassemble, decompile, or translate the Product or attempt to derive the source code of the Software or any non-public APIs;",
                        "publicly disseminate information regarding the performance of the Product;",
                        "access the Product to build a competitive product or service;",
                        "submit any viruses, worms or other harmful code to the Product or otherwise interfere with or cause harm to the Product;",
                        "seek to access information or data of other Paymeo customers;",
                        "use the Product to transmit spam or other unsolicited email; or",
                        "use the Product except as expressly permitted herein."
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-4 h-4 lg:w-5 lg:h-5 text-red-500 mr-2 lg:mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                          <span className="text-gray-600 text-sm lg:text-base">{item}</span>
                        </li>
                      ))}
                    </ul>
                  </section>

                  {/* Escrow Services & Transaction Terms */}
                  <section 
                    id="escrow-services"
                    ref={el => sectionRefs.current['escrow-services'] = el}
                    className="mb-8 lg:mb-12 scroll-mt-20 lg:scroll-mt-24"
                  >
                    <h2 className="text-xl lg:text-3xl font-bold text-gray-900 mb-4 lg:mb-6 pb-2 border-b-2 border-blue-100">Escrow Services & Transaction Terms</h2>
                    <p className="text-gray-600 leading-relaxed text-sm lg:text-base mb-4 lg:mb-6">
                      Paymeo provides escrow services to secure transactions between buyers and sellers. When a buyer initiates a transaction, funds are held in escrow until:
                    </p>
                    <ul className="grid gap-2 lg:gap-3 mb-4 lg:mb-6">
                      {[
                        "The buyer confirms satisfactory delivery/completion of goods/services",
                        "The seller provides proof of delivery",
                        "Dispute resolution process is completed",
                        "Automatic release occurs after 7 days if no dispute is raised"
                      ].map((item, index) => (
                        <li key={index} className="flex items-start">
                          <svg className="w-4 h-4 lg:w-5 lg:h-5 text-gray-500 mr-2 lg:mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                          </svg>
                          <span className="text-gray-600 text-sm lg:text-base">{item}</span>
                        </li>
                      ))}
                    </ul>
                    <div id="dispute-timeline" ref={el => sectionRefs.current['dispute-timeline'] = el} className="bg-blue-50 p-4 lg:p-6 rounded-xl border-l-4 border-blue-500">
                      <p className="text-gray-700 font-medium text-sm lg:text-base">
                        Buyers have 7 calendar days from delivery confirmation to raise disputes. Sellers are responsible for accurate product descriptions and delivery timelines.
                      </p>
                    </div>
                  </section>

             
                  {/* DISPUTE RESOLUTION */}
                  <section 
                    id="dispute-resolution"
                    ref={el => sectionRefs.current['dispute-resolution'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Dispute Resolution</h2>
                    
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 p-8 rounded-2xl border border-blue-100">
                      <h3 className="text-2xl font-bold text-gray-900 mb-6">Escrow Dispute Process</h3>
                      
                      <div className="space-y-6">
                        {/* Step 1 */}
                        <div id="initial-resolution" ref={el => sectionRefs.current['initial-resolution'] = el} className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">1</div>
                          <div className="ml-6">
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">Initial Resolution Attempt</h4>
                            <p className="text-gray-700 leading-relaxed">
                              Parties are encouraged to resolve disputes directly through the transaction chat within 24 hours.
                            </p>
                          </div>
                        </div>

                        {/* Step 2 */}
                        <div id="formal-dispute" ref={el => sectionRefs.current['formal-dispute'] = el} className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">2</div>
                          <div className="ml-6">
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">Formal Dispute Filing</h4>
                            <p className="text-gray-700 leading-relaxed mb-3">
                              If direct resolution fails, either party can file a formal dispute through the platform. Provide evidence including:
                            </p>
                            <ul className="text-gray-600 space-y-1">
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Order details and communication history
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Photos/videos of received goods
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Delivery confirmation/proof
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-blue-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Any other relevant documentation
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* Step 3 */}
                        <div id="ai-mediation" ref={el => sectionRefs.current['ai-mediation'] = el} className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">3</div>
                          <div className="ml-6">
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">AI-Assisted Mediation</h4>
                            <p className="text-gray-700 leading-relaxed mb-3">
                              Our AI system will analyze the dispute and may:
                            </p>
                            <ul className="text-gray-600 space-y-1">
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Initiate automated confirmation calls to verify delivery
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Suggest settlement options based on similar cases
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                                </svg>
                                Escalate to human review if complexity requires
                              </li>
                            </ul>
                          </div>
                        </div>

                        {/* Step 4 */}
                        <div id="human-review" ref={el => sectionRefs.current['human-review'] = el} className="flex items-start">
                          <div className="bg-blue-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">4</div>
                          <div className="ml-6">
                            <h4 className="text-xl font-semibold text-gray-900 mb-2">Human Review & Decision</h4>
                            <p className="text-gray-700 leading-relaxed mb-3">
                              For unresolved disputes, our support team will:
                            </p>
                            <ul className="text-gray-600 space-y-1">
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Review all evidence and communication
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Contact both parties for additional information
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Make a final binding decision within 5 business days
                              </li>
                              <li className="flex items-center">
                                <svg className="w-4 h-4 text-gray-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                Release escrow funds according to the decision
                              </li>
                            </ul>
                          </div>
                        </div>
                      </div>

                      <div className="mt-8 p-6 bg-yellow-50 rounded-xl border border-yellow-200">
                        <h4 className="font-bold text-yellow-800 mb-3 flex items-center">
                          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          Important Notes:
                        </h4>
                        <ul className="text-yellow-700 space-y-2">
                          <li className="flex items-start">
                            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Disputes must be filed within 7 days of delivery confirmation
                          </li>
                          <li className="flex items-start">
                            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Funds will remain in escrow until dispute resolution is complete
                          </li>
                          <li className="flex items-start">
                            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Repeated fraudulent disputes may result in account suspension
                          </li>
                          <li className="flex items-start">
                            <svg className="w-4 h-4 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            Our decision in dispute resolution is final and binding
                          </li>
                        </ul>
                      </div>
                    </div>
                  </section>

                  {/* Data Protection & Rights */}
                  <section 
                    id="data-protection"
                    ref={el => sectionRefs.current['data-protection'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Data Protection & Rights</h2>
                    <p className="text-gray-600 leading-relaxed mb-6">
                      Paymeo considers data protection and privacy to be of paramount importance. We will never sell personal data and we carry out all processing operations in strict compliance with the Nigeria Data Protection Act and other applicable regulations.
                    </p>
                    
                    <div id="information-collection" ref={el => sectionRefs.current['information-collection'] = el} className="mb-6">
                      <h3 className="text-2xl font-semibold  mb-4">Information Collection</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        When you sign up for and open an account with us, or sign up for content or offers, we may ask you to provide us with information such as your name, email address and details about your business to better provide you with our services. We also collect transaction data, chat histories, and location data for service delivery and dispute resolution.
                      </p>
                      
                      <p className="text-gray-600 leading-relaxed">
                        When you visit our site we store the name of your internet service provider, the website from which you visited us from, the parts of our site you visit, the date and duration of your visit, and information from the device (device type, operating system, screen resolution, language, country you are located in, and web browser type) you used during your visit. We process such usage data in an anonymized form for statistical purposes and to improve our site.
                      </p>
                    </div>
                    
                    <div id="user-rights" ref={el => sectionRefs.current['user-rights'] = el} className="bg-white p-6 rounded-xl border-l-4 border-blue-500">
                      <h3 className="text-2xl font-semibold  mb-4">Your Rights</h3>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        You may deactivate your Paymeo account and/or unsubscribe from receiving content or offers from us at any time. Following termination of your account, we may retain your personal data for a period of 90 calendar days after which it would be deleted permanently, except for transaction records required by financial regulations.
                      </p>
                      <p className="text-gray-600 leading-relaxed mb-4">
                        You have a right to be informed of personal data processed by Paymeo, a right to rectification/correction, erasure and restriction of personal data. You also have the right to receive from Paymeo a structured, common and machine-readable format of Personal Data you provided to us.
                      </p>
                      <p className="text-gray-600 leading-relaxed">
                        To exercise any of these rights, please contact our support team at <a href="mailto:company@paymeo.co" className="text-blue-600 hover:underline font-medium">company@paymeo.co</a>.
                      </p>
                    </div>
                  </section>

                  {/* Business Verification */}
                  <section 
                    id="business-verification"
                    ref={el => sectionRefs.current['business-verification'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Business Verification</h2>
                    <p className="text-gray-600 leading-relaxed">
                      In an attempt to curb frauds related to online payments and other services, we may request additional information from you at any time. We may also require your Bank Verification Number (BVN), CAC registration details, a government issued identification such as a passport or driver's license, or a business license. If you refuse any of these requests, your account may be suspended or terminated. We reserve the full rights to suspend or terminate an account of any user who provides inaccurate, untrue, or incomplete information required under this Agreement.
                    </p>
                  </section>

                  {/* Prohibited Use */}
                  <section 
                    id="prohibited-use"
                    ref={el => sectionRefs.current['prohibited-use'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Prohibited Use</h2>
                    <p className="text-gray-600 leading-relaxed">
                      The users shall use the services provided by this platform for lawful purposes only and not for the purpose of committing or furthering fraudulent acts or for committing any acts that would give rise to both or either civil and criminal liability. The users agree not to publish, post, upload, distribute, provide or enter any material or information that is illegal, unlawful or can be regarded as fraudulent, libelous, malicious, threatening, offensive, profane, obscene, fanning ethnic or racial tensions, immoral or any such information which any reasonable person would consider objectionable on grounds of good conscience. We reserve the full rights to suspend or terminate an account of any user who uses the platform for any fraudulent or illegal act.
                    </p>
                  </section>

                  {/* Subscription Fees & Charges */}
                  <section 
                    id="subscription-fees"
                    ref={el => sectionRefs.current['subscription-fees'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Subscription Fees & Charges</h2>
                    <p className="text-gray-600 leading-relaxed">
                      Subscription fees and any other charges for the use of the Application are described on the Site. They may change from time to time. If subscription fees change, we will give you at least 30 days' notice. If they do change, your continued use of the Application after the change indicates your agreement with the new fees and charges after the effective date of the change. Fees for payment transactions are subject to change without notice, unless otherwise prohibited, at the discretion of our payment platform partners. Our current subscription fees and charges can be found on our pricing page.
                    </p>
                  </section>

                  {/* Refund Policy */}
      {/* Refund Policy */}
<section 
  id="refund-policy"
  ref={el => sectionRefs.current['refund-policy'] = el}
  className="mb-12 scroll-mt-24"
>
  <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Refund Policy</h2>
  
  <div className="grid md:grid-cols-2 gap-6 mb-8">
    {/* For Sellers */}
    <div id="for-sellers" ref={el => sectionRefs.current['for-sellers'] = el} className="bg-gradient-to-br from-blue-50 to-indigo-50 p-4 rounded-xl border-l-4 border-blue-500">
      <div className="flex items-center mb-4">
        <div className="bg-blue-100 p-3 rounded-lg mr-4">
          <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
          </svg>
        </div>
        <h3 className="text-2xl font-semibold ">For Sellers & Vendors</h3>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-red-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
          </svg>
          <p className="text-gray-700">
            <strong>No subscription refunds:</strong> Payments made for any subscription plan or modules are non-refundable once processed by our payment gateway, regardless of billing cycle (monthly or yearly).
          </p>
        </div>
        
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700">
            <strong>Account changes:</strong> When you cancel your account or downgrade to a Free plan, you won't be charged again, except for any outstanding amounts incurred before the change.
          </p>
        </div>
        
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700">
            <strong>Escrow transactions:</strong> Refunds for marketplace transactions are processed according to dispute resolution outcomes and are deducted from your seller balance.
          </p>
        </div>
      </div>
    </div>

    {/* For Buyers */}
    <div id="for-buyers" ref={el => sectionRefs.current['for-buyers'] = el} className="bg-gradient-to-br from-gray-50 to-gray-50 p-4 rounded-xl border-l-4 border-gray-500">
      <div className="flex items-center mb-4">
        <div className="bg-gray-100 p-3 rounded-lg mr-4">
          <svg className="w-6 h-6 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M5 9h14l1 12H4L5 9z" />
          </svg>
        </div>
        <h3 className="text-2xl font-semibold ">For Buyers</h3>
      </div>
      
      <div className="space-y-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700">
            <strong>Full refund protection:</strong> You are eligible for full refunds or item replacement for non digital items in cases of verified seller faults including:
          </p>
        </div>
        
        <ul className="ml-8 space-y-2 text-gray-600">
          <li className="flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Faulty or defective products
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Wrong items delivered
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Items not as described
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Non-delivery of paid items
          </li>
          <li className="flex items-center">
            <svg className="w-4 h-4 text-red-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
            Significant delays without communication
          </li>
        </ul>
        
        <div className="flex items-start">
          <svg className="w-5 h-5 text-gray-500 mr-3 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-gray-700">
            <strong>Escrow protection:</strong> Your funds are held securely in escrow until you confirm satisfactory delivery. Refunds are processed automatically upon verified dispute resolution.
          </p>
        </div>
      </div>
    </div>
  </div>

  {/* Complaint Resolution Process */}
  <div className="bg-gradient-to-r from-purple-50 to-pink-50 p-8 rounded-2xl border border-purple-100" ref={el => sectionRefs.current['complaint-and-refund'] = el} id='complaint-and-refund'>
    <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center">
      <svg className="w-6 h-6 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
      Complaint & Refund Resolution Process
    </h3>

    <div className="space-y-6">
      {/* Step 1 */}
      <div className="flex items-start">
        <div className="bg-purple-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">1</div>
        <div className="ml-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Report & Documentation</h4>
          <p className="text-gray-700 mb-3">
            When you report an issue, we immediately:
          </p>
          <ul className="text-gray-600 space-y-1 ml-4">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Record your contact details and issue nature
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Generate a unique reference number for tracking
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Send immediate confirmation with your case details
            </li>
          </ul>
        </div>
      </div>

      {/* Step 2 */}
      <div className="flex items-start">
        <div className="bg-purple-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">2</div>
        <div className="ml-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Initial Resolution (Within 24 Hours)</h4>
          <p className="text-gray-700 mb-3">
            Our customer service team attempts immediate resolution:
          </p>
          <ul className="text-gray-600 space-y-1 ml-4">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Direct communication with both parties
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Evidence collection and verification
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Regular email updates on resolution progress
            </li>
          </ul>
        </div>
      </div>

      {/* Step 3 */}
      <div className="flex items-start">
        <div className="bg-purple-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">3</div>
        <div className="ml-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Escalation & Investigation (24-48 Hours)</h4>
          <p className="text-gray-700 mb-3">
            If unresolved, our Issue Resolution Team takes over:
          </p>
          <ul className="text-gray-600 space-y-1 ml-4">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Comprehensive investigation of all circumstances
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Additional information requests if needed
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Findings communicated within 48 hours of initial contact
            </li>
          </ul>
        </div>
      </div>

      {/* Step 4 */}
      <div className="flex items-start">
        <div className="bg-purple-600 text-white rounded-xl w-10 h-10 flex items-center justify-center flex-shrink-0 mt-1 font-bold shadow-lg">4</div>
        <div className="ml-6">
          <h4 className="text-xl font-semibold text-gray-900 mb-2">Final Resolution (Within 5 Days)</h4>
          <p className="text-gray-700 mb-3">
            Customer Experience Team provides final resolution:
          </p>
          <ul className="text-gray-600 space-y-1 ml-4">
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Automatic escalation if not resolved in 48 hours
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Final binding decision and refund processing
            </li>
            <li className="flex items-center">
              <svg className="w-4 h-4 text-purple-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Satisfaction confirmation and case closure
            </li>
          </ul>
        </div>
      </div>
    </div>

    <div className="mt-6 p-4 bg-white rounded-lg border border-purple-200">
      <div className="flex items-center">
        <svg className="w-5 h-5 text-purple-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <p className="text-purple-700 font-medium">
          Ready to request a refund? <i onClick={ () => {navigate("/request-refund")}} className="underline hover:text-purple-800">Visit our refund request page</i> to start the process.
        </p>
      </div>
    </div>
  </div>

 
</section>
                  {/* Online Payments */}
                  <section 
                    id="online-payments"
                    ref={el => sectionRefs.current['online-payments'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Online Payments</h2>
                    <p className="text-gray-600 leading-relaxed">
                      We allow users to receive online payments from customers ("End Customers") on our website. These payments are processed through any of our payment partners (Paystack, Flutterwave, or Moniepoint). In special cases where there is a CHARGEBACK or DISPUTE associated with any of your received payment transactions, you may be required to provide an evidence for value exchange between you and the customer and if you fail to do so, you will be forcefully required to refund such amount or its equivalent to the customer. Any purchase or payment is solely between you and the End Customer, Paymeo is not party to your transactions and assumes no liability or responsibility for your products, services or offerings.
                    </p>
                  </section>

                  {/* API Access */}
                  <section 
                    id="api-access"
                    ref={el => sectionRefs.current['api-access'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">API Access</h2>
                    <p className="text-gray-600 leading-relaxed">
                      Paymeo provides extended access to the application via its Application Programming Interface (API) as a part of our Service. Subject to the other terms of this agreement, we grant Customer a non-exclusive, nontransferable, terminable license to interact with the API only for purposes of the Service as allowed by the API. Customer may not use the API in a manner that fails to comply with the API technical documentation or with any part of the API. If any of these occur, Paymeo can decide to temporarily or permanently suspend/terminate Customer's access to the API without any prior notice.
                    </p>
                  </section>

                  {/* Cancellation Of Accounts */}
                  <section 
                    id="cancellation-accounts"
                    ref={el => sectionRefs.current['cancellation-accounts'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Cancellation Of Accounts</h2>
                    <p className="text-gray-600 leading-relaxed">
                      The user has the right to delete or cancel their account at any time. Once the account is cancelled all information associated with that account will no longer be accessible on the website. Paymeo has no obligation to maintain the Customer Data and may destroy it after 60-days period. Each user is solely responsible for account cancellation or de-activation. Paymeo reserves the right to terminate or suspend any account at any time and deny future service to anyone for any reason.
                    </p>
                  </section>

                  {/* Use of Cookies */}
                  <section 
                    id="use-cookies"
                    ref={el => sectionRefs.current['use-cookies'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Use of Cookies</h2>
                    <p className="text-gray-600 leading-relaxed">
                      We use cookies to identify you as a User and make your user experience easier, customize our services, content and advertising; help you ensure that your account security is not compromised, mitigate risk and prevent fraud; and to promote trust and safety on our website. Cookies allow our servers to remember your account log-in information when you visit our website, IP addresses, date and time of visits, monitor web traffic and prevent fraudulent activities. If your browser or browser add-on permits, you have the choice to disable cookies on our website, however this may limit your ability to use our website.
                    </p>
                  </section>

                  {/* Disclaimer & Limitation of Liability */}
                  <section 
                    id="disclaimer-liability"
                    ref={el => sectionRefs.current['disclaimer-liability'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Disclaimer & Limitation of Liability</h2>
                    <p className="text-gray-600 leading-relaxed">
                      Paymeo makes no representations or warranties of any kind, express or implied, as to the operation of the site or the information, content, materials, or products included on this site except as provided here to the full extent permissible by applicable law, Paymeo disclaims all warranties, express or implied, including, but not limited to, implied warranties of merchantability and fitness for a particular purpose, non-infringement, title, quiet enjoyment, data accuracy, and system integration. "You acknowledge and agree that the use of the Site is at your own risk as the services are available without warranties." This site may include inaccuracies, mistakes or typographical errors. Paymeo does not warrant that the content will be uninterrupted or error free. To the maximum extent permitted by law, Paymeo will not be liable for any damages of any kind arising from the use of this site, including, but not limited to, indirect, incidental, punitive, exemplary, special, or consequential damages. "We are committed to conducting our business in accordance with these principles in order to ensure that the confidentiality of personal information is protected and maintained."
                    </p>
                  </section>

                  {/* Copyright */}
                  <section 
                    id="copyright"
                    ref={el => sectionRefs.current.copyright = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Copyright</h2>
                    <p className="text-gray-600 leading-relaxed mb-4">
                      All text, graphics, photographs, button icons, audio clips, logos, slogans, trade names or word software and other contents on this Site collectively belongs exclusively to Paymeo or its appropriate content suppliers. You may not use, reproduce, copy, modify, transmit, display, publish, sell, license, publicly perform, distribute or commercially exploit any of the Content or otherwise dispose of any of the Content in a way not permitted by Paymeo, without express prior written consent.
                    </p>
                    <p className="text-gray-600 leading-relaxed">
                      The use of data mining, robots, or similar data gathering and extraction tools on this Site is strictly prohibited. You may view and use the Content only for your personal information and for the sole purpose of the services rendered by Paymeo. The Content, the Compilation and the Software are all protected under the law of the Federal Republic of Nigeria. All rights not expressly granted are reserved by Paymeo. Violators will be prosecuted to the full extent of the law.
                    </p>
                  </section>

                  {/* Applicable Law */}
                  <section 
                    id="applicable-law"
                    ref={el => sectionRefs.current['applicable-law'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Applicable Law</h2>
                    <p className="text-gray-600 leading-relaxed">
                      This Agreement shall be interpreted and construed in accordance with the laws of the Federal Republic of Nigeria, without regard to the conflicts of laws principles thereof.
                    </p>
                  </section>

                  {/* Electronic Signature */}
                  <section 
                    id="electronic-signature"
                    ref={el => sectionRefs.current['electronic-signature'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Electronic Signature</h2>
                    <p className="text-gray-600 leading-relaxed">
                      With respect to these Terms, you hereby waive any applicable rights to require an original (non-electronic) signature or delivery or retention of non-electronic records, to the extent not prohibited under applicable law.
                    </p>
                  </section>

                  {/* Updates and Modifications */}
                  <section 
                    id="updates-modifications"
                    ref={el => sectionRefs.current['updates-modifications'] = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Updates and Modifications</h2>
                    <p className="text-gray-600 leading-relaxed">
                      Paymeo may revise and update these Terms and Conditions at anytime. Please periodically review the Terms and Conditions because your continued usage of the web site after any changes to these Terms and Conditions will mean you accept those changes.
                    </p>
                  </section>

                  {/* Feedback */}
                  <section 
                    id="feedback"
                    ref={el => sectionRefs.current.feedback = el}
                    className="mb-12 scroll-mt-24"
                  >
                    <h2 className="text-3xl font-bold text-gray-900 mb-6 pb-2 border-b-2 border-blue-100">Feedback</h2>
                    <p className="text-gray-600 leading-relaxed">
                      If you have questions about any of this User Agreements or you wish to report breaches of the User Agreement in this Terms of Condition, please contact Us via E-mail: <a href="mailto:company@paymeo.co" className="text-blue-600 hover:underline font-medium">company@paymeo.co</a> with "Terms & Condition" in the subject line.
                    </p>

                     {/* Contact Information */}
  <div className=" text-left w-full">
    <div className="inline-flex items-center py-4 rounded-xl">
      <svg className="w-5 h-5 text-gray-600 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
      <div className="text-left">
        <p className="text-gray-700 font-medium">Need immediate assistance?</p>
        <p className="text-gray-600">
          Contact us at <a href="mailto:company@paymeo.co" className="text-blue-600 hover:underline font-medium">company@paymeo.co</a> or use the chat box for instant support.
        </p>
      </div>
    </div>
  </div>
                  </section>


                  {/* Continue with other sections following the same mobile-optimized pattern... */}
                  
                  {/* Footer */}
                  <div className="border-t border-gray-200 pt-6 lg:pt-8 text-center mt-8 lg:mt-12">
                    <p className="text-gray-500 mb-4 lg:mb-6 text-sm lg:text-base">This Terms of Service and Privacy Policy was last updated on November 2025.</p>
                    <Link 
                      to="https://web.paymeo.co/login" 
                      className="inline-flex items-center px-6 lg:px-8 py-3 lg:py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-semibold rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-0.5 hover:from-blue-700 hover:to-blue-800 text-sm lg:text-base"
                    >
                      Sign up for free today, no credit card required
                      <svg className="w-4 h-4 lg:w-5 lg:h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
                      </svg>
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <SmartsuppChat /> 
      </div>
    </div>
  );
};

export default TermsOfService;